import React, { useContext, useState, useMemo, useCallback, useEffect, memo } from 'react';
import {
  Typography,
  Box,
  Card,
  Button,
  Chip,
  Container,
  Grid,
  LinearProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Fade,
  Stack,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { WalletContext, getExplorerFor, switchToChain } from './WalletConnect';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CasinoIcon from '@mui/icons-material/Casino';
import LockIcon from '@mui/icons-material/Lock';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import StarIcon from '@mui/icons-material/Star';
import DiamondIcon from '@mui/icons-material/Diamond';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShieldIcon from '@mui/icons-material/Shield';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BoltIcon from '@mui/icons-material/Bolt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VerifiedIcon from '@mui/icons-material/Verified';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { withAlpha } from './gamepage/gameUtils'; // corners now come from theme CSS vars

/* ------------------------ Utilities ------------------------ */
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const formatCompact = (n) =>
  Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(n || 0));

const BASE_CHAIN_ID = 8453;

const useLocalStorage = (key, initialValue) => {
  const [val, setVal] = useState(() => {
    try {
      if (typeof window === 'undefined') return initialValue;
      const raw = window.localStorage.getItem(key);
      return raw !== null ? JSON.parse(raw) : initialValue;
    } catch {
      return initialValue;
    }
  });
  useEffect(() => {
    try {
      if (typeof window !== 'undefined')
        window.localStorage.setItem(key, JSON.stringify(val));
    } catch {}
  }, [key, val]);
  return [val, setVal];
};

/* Pick an icon by reading the perk text */
const PerkIcon = memo(function PerkIcon({ text, color }) {
  const t = String(text || '').toLowerCase();
  const sx = { fontSize: 18, color };
  if (t.includes('rake') || t.includes('cashback')) return <EmojiEventsIcon sx={sx} />;
  if (t.includes('boost') || t.includes('multiplier')) return <TrendingUpIcon sx={sx} />;
  if (t.includes('quest') || t.includes('mission')) return <RocketLaunchIcon sx={sx} />;
  if (t.includes('vip') || t.includes('tier') || t.includes('badge')) return <WorkspacePremiumIcon sx={sx} />;
  if (t.includes('priority') || t.includes('support')) return <SupportAgentIcon sx={sx} />;
  if (t.includes('fast') || t.includes('instant') || t.includes('speed')) return <BoltIcon sx={sx} />;
  if (t.includes('secure') || t.includes('insurance') || t.includes('protection')) return <ShieldIcon sx={sx} />;
  if (t.includes('bonus') || t.includes('gift')) return <CardGiftcardIcon sx={sx} />;
  if (t.includes('allowance') || t.includes('limit') || t.includes('unlock')) return <LockOpenIcon sx={sx} />;
  if (t.includes('verified') || t.includes('trusted')) return <VerifiedIcon sx={sx} />;
  if (t.includes('diamond')) return <DiamondIcon sx={sx} />;
  if (t.includes('star') || t.includes('elite')) return <StarIcon sx={sx} />;
  if (t.includes('earn') || t.includes('payout') || t.includes('profit')) return <AttachMoneyIcon sx={sx} />;
  if (t.includes('hot') || t.includes('streak')) return <LocalFireDepartmentIcon sx={sx} />;
  return <StarIcon sx={sx} />;
});

/* ------------------------ VIP Rank Icons ------------------------ */
const levelIconSrc = (level) => {
  const name = String(level || '').toLowerCase();
  if (name.includes('mythic')) return '/crystalfour.svg';
  if (name.includes('void'))   return '/crystalthree.svg';
  if (name.includes('flux'))   return '/crystaltwo.svg';
  if (name.includes('titan'))  return '/crystalone.svg';
  // fallback
  return '/crystalone.svg';
};

function LevelIcon({ level, color, size = '3.2rem' }) {
  const src = levelIconSrc(level);
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        backgroundColor: color || '#ffffff',
        WebkitMask: `url(${src}) no-repeat center / contain`,
        mask: `url(${src}) no-repeat center / contain`,
        display: 'inline-block',
        filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.28))',
      }}
    />
  );
}

/* ------------------------ Card ------------------------ */
const VipCard = memo(function VipCard({
  theme,
  lvl,
  index,
  currentIndex,
  vipProgress,
  isMaxLevel,
  totalXP,
  favoritesSet,
  onToggleFavorite,
  onOpenDetails,
}) {
  const reduce = prefersReducedMotion();
  const isCurrent = index === currentIndex;
  const isUnlocked = index <= currentIndex;
  const progress = index < currentIndex ? 100 : isCurrent ? vipProgress : 0;
  const remaining = isCurrent && !isMaxLevel ? Math.max(0, lvl.nextNeed - totalXP) : 0;

  let captionText;
  if (index > currentIndex) captionText = `${lvl.need.toLocaleString()} CPT required`;
  else if (index < currentIndex) captionText = 'Unlocked';
  else captionText = isMaxLevel ? 'Top Tier Achieved' : `${remaining.toLocaleString()} CPT to ${lvl.nextLevel}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: !reduce && isCurrent ? 1.04 : 1 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      whileHover={!reduce ? { scale: 1.06 } : undefined}
    >
      <Card
        sx={{
          p: { xs: 2, md: 3 },
          textAlign: 'center',
          border: `1px solid ${withAlpha(lvl.color, 0.5)}`,
          borderRadius: 'var(--radius-card)',
          position: 'relative',
          width: '100%',
          maxWidth: 340,
          height: { xs: 'auto', md: 400 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          boxShadow: '0 10px 28px rgba(0,0,0,0.28)',
          background: `linear-gradient(180deg, ${withAlpha(lvl.color, 0.12)}, rgba(255,255,255,0.04))`,
          opacity: isUnlocked ? 1 : 0.85,
          overflow: 'hidden',
          cursor: isUnlocked ? 'pointer' : 'default',
        }}
        aria-label={`${lvl.level} VIP Tier`}
        role={isUnlocked ? 'button' : 'group'}
        aria-disabled={!isUnlocked}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (isUnlocked) onOpenDetails(lvl, index);
          }
        }}
        onClick={(e) => {
          if (!isUnlocked) return;
          try { e.preventDefault(); e.stopPropagation(); } catch {}
          onOpenDetails(lvl, index);
        }}
      >
        {/* Rakeback chip */}
        {lvl.rake > 0 && (
          <Chip
            icon={<CasinoIcon sx={{ fontSize: 14 }} />}
            label={`${lvl.rake}% Rakeback`}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              bgcolor: lvl.color,
              color: '#000',
              fontWeight: 800,
              borderRadius: 'var(--radius-chip)',
            }}
          />
        )}

        {/* Favorite toggle */}
        <IconButton
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(lvl); }}
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            color: favoritesSet.has(lvl.level) ? '#000' : theme.colors.text,
            bgcolor: favoritesSet.has(lvl.level) ? lvl.color : 'transparent',
            border: favoritesSet.has(lvl.level)
              ? 'none'
              : `1px solid ${withAlpha(lvl.color, 0.45)}`,
            borderRadius: 'var(--radius-button)',
            boxShadow: favoritesSet.has(lvl.level)
              ? `0 0 0 1px ${withAlpha(lvl.color, 0.18)} inset, 0 8px 18px rgba(0,0,0,0.28)`
              : 'none',
            transition:
              'transform .15s ease, background-color .15s ease, box-shadow .15s ease',
            '&:hover': {
              transform: 'scale(1.05)',
              bgcolor: favoritesSet.has(lvl.level)
                ? lvl.color
                : withAlpha(lvl.color, 0.12),
            },
            '&:focus-visible': {
              outline: `2px solid ${lvl.color}`,
              outlineOffset: 2,
            },
          }}
          aria-label={
            favoritesSet.has(lvl.level)
              ? 'Remove from favorites'
              : 'Add to favorites'
          }
          aria-pressed={favoritesSet.has(lvl.level)}
        >
          {favoritesSet.has(lvl.level) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
        </IconButton>

        {/* Lock hint for future tiers */}
        {!isUnlocked && (
          <IconButton
            sx={{
              position: 'absolute',
              top: 12,
              right: 44,
              color: theme.colors.subText,
              pointerEvents: 'none',
            }}
            aria-label="Locked"
          >
            <LockIcon />
          </IconButton>
        )}

        {/* Content */}
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="h6"
            sx={{
              color: lvl.color,
              mb: 1.5,
              fontSize: { xs: '1.25rem', md: '1.375rem' },
              fontWeight: 800,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {lvl.level}
          </Typography>

          {/* Progress (global shine is on by CSS) */}
          <LinearProgress
            variant="determinate"
            value={progress}
            aria-label={`Progress ${Math.round(progress)}%`}
            sx={{
              height: 10,
              borderRadius: 'var(--radius-card)',
              mb: 1,
              backgroundColor: withAlpha(lvl.color, 0.165),
              '& .MuiLinearProgress-bar': {
                backgroundColor: lvl.color,
                borderRadius: 'var(--radius-card)',
              },
            }}
          />

          <Typography
            variant="caption"
            sx={{
              color: theme.colors.text,
              mb: 1.25,
              display: 'block',
              fontWeight: 600,
            }}
            aria-live="polite"
          >
            {captionText}
          </Typography>

          <Box sx={{ mt: 0.25, mb: 0.75, display: 'grid', placeItems: 'center' }}>
            <LevelIcon level={lvl.level} color={lvl.color} size={{ xs: '3rem', md: '3.6rem' }} />
          </Box>

          {/* Quick chips */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 0.5,
              mb: 0.75,
            }}
          >
            <Chip
              label={`${lvl.need.toLocaleString()} CPT min`}
              size="small"
              sx={{
                bgcolor: withAlpha(lvl.color, 0.078),
                color: theme.colors.text,
                borderRadius: 'var(--radius-chip)',
                border: `1px solid ${withAlpha(lvl.color, 0.333)}`,
              }}
            />
            {typeof lvl.rake === 'number' && (
              <Chip
                label={`${lvl.rake}% rakeback`}
                size="small"
                sx={{
                  bgcolor: withAlpha(theme.colors.primary, 0.078),
                  color: theme.colors.text,
                  borderRadius: 'var(--radius-chip)',
                  border: `1px solid ${withAlpha(theme.colors.primary, 0.333)}`,
                }}
              />
            )}
          </Box>
        </Box>

        {/* Top perks */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            mt: 0.5,
            px: 0.5,
          }}
        >
          {lvl.perks.slice(0, 3).map((perk, i) => (
            <Tooltip key={i} title={perk.text} arrow placement="top">
              <Box
                sx={{
                  mb: 0.7,
                  color: theme.colors.text,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'default',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 28,
                    height: 28,
                    bgcolor: withAlpha(lvl.color, 0.125),
                    borderRadius: 'var(--radius-card)',
                  }}
                >
                  <PerkIcon text={perk.text} color={lvl.color} />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    color: theme.colors.text,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.3,
                  }}
                >
                  {perk.text}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
          <Button
            variant={isUnlocked ? 'contained' : 'outlined'}
            size="small"
            onClick={(e) => { e.stopPropagation(); onOpenDetails(lvl, index); }}
            sx={{
              borderColor: lvl.color,
              color: isUnlocked ? '#000' : lvl.color,
              bgcolor: isUnlocked ? lvl.color : 'transparent',
              fontWeight: 800,
              textTransform: 'none',
              borderRadius: 'var(--radius-button)',
              minHeight: { xs: 32, md: 28 },
              px: { xs: 1.25, md: 1 },
              fontSize: { xs: '0.8125rem', md: '0.75rem' },
              letterSpacing: { xs: 0.1, md: 0 },
              boxShadow: 'none',
            }}
          >
            {isCurrent ? 'Current' : isUnlocked ? 'View' : 'Locked'}
          </Button>

          <Tooltip title="Play eligible games to earn VIP XP faster">
            <Button
              variant="outlined"
              size="small"
              aria-label="Open eligible games to boost XP"
              component={RouterLink}
              to="/games/honeypot"
              onClick={(e) => e.stopPropagation()}
              endIcon={<ArrowForwardIosIcon />}
              sx={{
                borderColor: theme.colors.accent,
                color: theme.colors.text,
                fontWeight: 800,
                textTransform: 'none',
                borderRadius: 'var(--radius-button)',
                minHeight: { xs: 32, md: 28 },
                px: { xs: 1.25, md: 1 },
                fontSize: { xs: '0.8125rem', md: '0.75rem' },
                letterSpacing: { xs: 0.1, md: 0 },
                '& .MuiButton-endIcon > *:nth-of-type(1)': { fontSize: { xs: 16, md: 14 } },
              }}
            >
              Earn Faster
            </Button>
          </Tooltip>
        </Box>
      </Card>
    </motion.div>
  );
});

VipCard.propTypes = {
  theme: PropTypes.object.isRequired,
  lvl: PropTypes.shape({
    level: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    need: PropTypes.number.isRequired,
    rake: PropTypes.number.isRequired,
    perks: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        icon: PropTypes.string.isRequired,
      })
    ).isRequired,
    nextNeed: PropTypes.number,
    nextLevel: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  currentIndex: PropTypes.number.isRequired,
  vipProgress: PropTypes.number.isRequired,
  isMaxLevel: PropTypes.bool.isRequired,
  totalXP: PropTypes.number.isRequired,
  favoritesSet: PropTypes.instanceOf(Set).isRequired,
  onToggleFavorite: PropTypes.func.isRequired,
  onOpenDetails: PropTypes.func.isRequired,
};

/* ------------------------ Page ------------------------ */
const VipClub = ({ theme, vipLevels, userStats, isLoading = false, onOpenTier }) => {
  const { balance, address } = useContext(WalletContext);

  /* Chain state */
  const [chainId, setChainId] = useState(null);
  const networkOk = useMemo(() => {
    try {
      const n = (typeof chainId === 'string' && chainId?.startsWith?.('0x')) ? parseInt(chainId, 16) : Number(chainId);
      return n === BASE_CHAIN_ID;
    } catch {
      return false;
    }
  }, [chainId]);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cid =
          (typeof window !== 'undefined' && window.ethereum?.request)
            ? await window.ethereum.request({ method: 'eth_chainId' })
            : null;
        if (mounted) setChainId(cid ?? null);
      } catch {}
    })();
    const onChainChanged = (cid) => { try { setChainId(cid); } catch {} };
    try { window.ethereum?.on?.('chainChanged', onChainChanged); } catch {}
    return () => { mounted = false; try { window.ethereum?.removeListener?.('chainChanged', onChainChanged); } catch {} };
  }, []);

  const currentChainId = useMemo(() => {
    try {
      if (typeof chainId === 'string' && chainId?.startsWith?.('0x')) return parseInt(chainId, 16);
      const n = Number(chainId);
      return Number.isFinite(n) && n > 0 ? n : BASE_CHAIN_ID;
    } catch {
      return BASE_CHAIN_ID;
    }
  }, [chainId]);

  const explorer = useMemo(() => getExplorerFor(currentChainId), [currentChainId]);

  const onSwitchChain = useCallback(async () => {
    try {
      await switchToChain('base');
    } catch {}
  }, []);

  /* Player XP from wallet */
  const totalXP = useMemo(() => {
    const cpt = address && balance?.cpt ? parseFloat(balance.cpt) || 0 : 0;
    return cpt;
  }, [address, balance?.cpt]);

  /* Favorites */
  const [favorites, setFavorites] = useLocalStorage('cpt_vip_favorites', []);
  const favoritesSet = useMemo(() => new Set(favorites.map((f) => f.level)), [favorites]);

  /* Normalize vipLevels for nextNeed/nextLevel */
  const safeVipLevels = useMemo(() => {
    const list = Array.isArray(vipLevels) ? [...vipLevels] : [];
    for (let i = 0; i < list.length; i++) {
      const next = list[i + 1];
      list[i] = {
        ...list[i],
        nextNeed: next?.need ?? list[i].need,
        nextLevel: next?.level ?? list[i].level,
      };
    }
    return list;
  }, [vipLevels]);

  // Pre-sort levels to avoid recomputing on every render (depends on safeVipLevels)
  const sortedLevels = useMemo(() => {
    const arr = Array.isArray(safeVipLevels) ? [...safeVipLevels] : [];
    arr.sort((a, b) => {
      const fa = favoritesSet.has(a.level) ? 1 : 0;
      const fb = favoritesSet.has(b.level) ? 1 : 0;
      if (fb !== fa) return fb - fa; // favorites first
      return a.need - b.need;
    });
    return arr;
  }, [safeVipLevels, favoritesSet]);

  /* Current tier & progress */
  const currentIndex = useMemo(() => {
    if (!safeVipLevels.length) return -1;
    let idx = 0;
    for (let i = 1; i < safeVipLevels.length; i++) {
      if (totalXP >= safeVipLevels[i].need) idx = i;
      else break;
    }
    return idx;
  }, [safeVipLevels, totalXP]);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        window.__VIP_LEVELS__ = safeVipLevels.map((v) => ({
          level: v.level,
          need: Number(v.need) || 0,
        }));
        window.__VIP_CURRENT__ = {
          level: safeVipLevels[currentIndex]?.level || null,
          index: currentIndex,
          totalXP,
        };
      }
    } catch {}
  }, [safeVipLevels, currentIndex, totalXP]);

  const isMaxLevel = currentIndex === safeVipLevels.length - 1;
  const nextVip = isMaxLevel ? null : safeVipLevels[currentIndex + 1];
  const vipProgress = useMemo(() => {
    if (!safeVipLevels.length) return 0;
    if (isMaxLevel) return 100;
    const currentNeed = safeVipLevels[currentIndex]?.need || 0;
    const nextNeed = nextVip?.need || currentNeed;
    if (nextNeed === currentNeed) return 100;
    const pct = ((totalXP - currentNeed) / (nextNeed - currentNeed)) * 100;
    return Math.min(100, Math.max(0, pct));
  }, [safeVipLevels, currentIndex, isMaxLevel, nextVip, totalXP]);

  /* a11y announce updates (uses global live region window.srAnnounce) */
  useEffect(() => {
    try {
      const lvl = safeVipLevels[Math.max(0, currentIndex)];
      const pct = Math.round(vipProgress);
      window.srAnnounce?.(`VIP ${lvl?.level || ''} progress ${pct} percent`);
    } catch {}
  }, [currentIndex, vipProgress, safeVipLevels]);

  const handleFavoriteToggle = useCallback(
    (level) => {
      setFavorites((prev) =>
        prev.some((f) => f.level === level.level)
          ? prev.filter((f) => f.level !== level.level)
          : [...prev, level]
      );
    },
    [setFavorites]
  );

  // Haptic hint on toggle (memoized)
  const onToggleFavoriteHaptic = useCallback((level) => {
    try { navigator.vibrate?.(6); } catch {}
    handleFavoriteToggle(level);
  }, [handleFavoriteToggle]);

  const [selectedLevel, setSelectedLevel] = useState(null);
  const openDetails = useCallback(
    (level, index) => {
      if (index <= currentIndex) {
        setSelectedLevel(level);
        onOpenTier?.(level);
      }
    },
    [currentIndex, onOpenTier]
  );
  const closeDetails = useCallback(() => setSelectedLevel(null), []);

  /* Loading / empty */
  if (isLoading) {
    return (
      <Box sx={{ bgcolor: theme.colors.surfaceAlt, py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Skeleton variant="text" width={220} height={60} sx={{ mx: 'auto' }} />
            <Skeleton variant="text" width={520} height={28} sx={{ mx: 'auto', mt: 1.5 }} />
          </Box>
          <Grid container spacing={4} justifyContent="center">
            {Array.from({ length: 4 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton variant="rounded" height={400} sx={{ borderRadius: 'var(--radius-card)' }} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  if (!safeVipLevels.length) {
    return (
      <Box sx={{ bgcolor: theme.colors.surfaceAlt, py: { xs: 8, md: 12 }, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: theme.colors.text }}>
          VIP program is temporarily unavailable.
        </Typography>
      </Box>
    );
  }

  const currentLvl = safeVipLevels[Math.max(0, currentIndex)];
  const isShrimp = (currentLvl?.level || '').toLowerCase() === 'titan';

  return (
    <Box sx={{ bgcolor: theme.colors.surfaceAlt, py: { xs: 8, md: 12 }, position: 'relative' }}>
      {/* subtle accent wash */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(50% 60% at 50% 20%, ${withAlpha(
            theme.colors.accent,
            0.08
          )}, transparent 60%)`,
          pointerEvents: 'none',
        }}
      />
      <Container maxWidth="lg">
        {/* Header / status card */}
        <Box
          sx={{
            mb: 4,
            p: { xs: 2, md: 3 },
            borderRadius: 'var(--radius-card)',
            border: `1px solid ${withAlpha(currentLvl.color || theme.colors.primary, 0.28)}`,
            background: theme.colors.surface,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(900px 360px at 8% -30%, ${withAlpha(
                currentLvl.color || theme.colors.primary,
                0.18
              )}, transparent 60%)`,
              borderRadius: 'inherit',
            }}
          />
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            sx={{ position: 'relative', zIndex: 1 }}
          >
            <Stack spacing={0.75} sx={{ minWidth: 220 }}>
              <Chip
                size="small"
                label="Your VIP Journey"
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: isShrimp
                    ? 'transparent'
                    : withAlpha(currentLvl.color || theme.colors.primary, 0.12),
                  color: theme.colors.text,
                  border: isShrimp
                    ? `1px solid ${theme.colors.border}`
                    : `1px solid ${withAlpha(
                        currentLvl.color || theme.colors.primary,
                        0.42
                      )}`,
                  borderRadius: 'var(--radius-chip)',
                  px: 1.25,
                  fontWeight: 800,
                }}
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <LevelIcon level={currentLvl.level} color={currentLvl.color} size={{ xs: '2.4rem', md: '2.8rem' }} />
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: '1.25rem',
                    color: theme.colors.text,
                  }}
                >
                  {currentLvl.level}
                </Typography>
                <Chip
                  size="small"
                  label="Current"
                  sx={{
                    bgcolor: withAlpha(
                      currentLvl.color || theme.colors.primary,
                      0.18
                    ),
                    color: theme.colors.text,
                    borderRadius: 'var(--radius-chip)',
                    border: `1px solid ${withAlpha(
                      currentLvl.color || theme.colors.primary,
                      0.35
                    )}`,
                    ml: 0.5,
                  }}
                />
              </Stack>
              <Typography sx={{ color: theme.colors.subText, fontSize: '0.92rem' }}>
                CPT Holdings:{' '}
                <strong style={{ color: theme.colors.text }}>
                  {formatCompact(totalXP)} CPT
                </strong>
              </Typography>
            </Stack>

            {/* Progress block */}
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 360 }, maxWidth: 640 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                <Typography sx={{ color: theme.colors.subText, fontSize: '0.82rem' }}>
                  {isMaxLevel ? 'Max tier reached' : `Progress to ${nextVip?.level}`}
                </Typography>
                <Typography sx={{ color: theme.colors.text, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                  {Math.round(vipProgress)}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={vipProgress}
                aria-label={`VIP progress ${Math.round(vipProgress)}%`}
                sx={{
                  height: 14,
                  borderRadius: 'var(--radius-card)',
                  backgroundColor: withAlpha(
                    currentLvl.color || theme.colors.primary,
                    0.15
                  ),
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: currentLvl.color || theme.colors.primary,
                    borderRadius: 'var(--radius-card)',
                  },
                }}
              />
              <Typography sx={{ color: theme.colors.subText, mt: 0.5, fontSize: '0.82rem' }}>
                {isMaxLevel
                  ? 'Enjoy premium rewards and events.'
                  : `${(nextVip?.need || 0).toLocaleString()} CPT required • ${Math.max(
                      0,
                      (nextVip?.need || 0) - totalXP
                    ).toLocaleString()} CPT to go`}
              </Typography>
            </Box>

            {/* Wallet / actions */}
            <Stack
              spacing={1}
              alignItems={{ xs: 'stretch', md: 'flex-end' }}
              direction={{ xs: 'column', md: 'row' }}
              sx={{ minWidth: { md: 320 } }}
            >
              {!networkOk && (
                <Button
                  size="small"
                  onClick={onSwitchChain}
                  sx={{
                    borderRadius: 'var(--radius-button)',
                    bgcolor: withAlpha(theme.colors.primary, 0.13),
                    color: theme.colors.text,
                    border: `1px solid ${withAlpha(theme.colors.primary, 0.4)}`,
                    textTransform: 'none',
                    px: 1.5,
                  }}
                >
                  Switch to Base
                </Button>
              )}
              {address && (
                <Button
                  component="a"
                  href={`${explorer}/address/${address}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  size="small"
                  sx={{
                    borderRadius: 'var(--radius-button)',
                    color: theme.colors.text,
                    border: `1px solid ${theme.colors.border}`,
                    textTransform: 'none',
                    px: 1.25,
                  }}
                >
                  Basescan: {address.slice(0, 6)}…{address.slice(-4)}
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => {
                  try {
                    if (typeof window !== 'undefined' && window.dispatchEvent)
                      window.dispatchEvent(new CustomEvent('open_wallet_connect'));
                  } catch {}
                }}
                size="small"
                sx={{
                  borderRadius: 'var(--radius-button)',
                  borderColor: theme.colors.primary,
                  color: theme.colors.text,
                  fontWeight: 800,
                }}
              >
                Connect / Switch Wallet
              </Button>
            </Stack>
          </Stack>

          {/* Feature hints */}
          <Stack
            direction="row"
            sx={{
              mt: 2,
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 1,
              rowGap: 1,
              alignItems: 'center',
            }}
          >
            <Chip
              icon={<EmojiEventsIcon />}
              label="Rakeback"
              size="small"
              variant={isShrimp ? 'outlined' : undefined}
              sx={{
                bgcolor: isShrimp ? 'transparent' : withAlpha(theme.colors.secondary, 0.12),
                color: theme.colors.text,
                borderRadius: 'var(--radius-chip)',
                border: isShrimp ? `1px solid ${theme.colors.border}` : `1px solid ${withAlpha(theme.colors.secondary, 0.42)}`,
              }}
            />
            <Chip
              icon={<CardGiftcardIcon />}
              label="Free Spins"
              size="small"
              variant={isShrimp ? 'outlined' : undefined}
              sx={{
                bgcolor: isShrimp ? 'transparent' : withAlpha(theme.colors.secondary, 0.12),
                color: theme.colors.text,
                borderRadius: 'var(--radius-chip)',
                border: isShrimp ? `1px solid ${theme.colors.border}` : `1px solid ${withAlpha(theme.colors.secondary, 0.42)}`,
              }}
            />
            <Chip
              icon={<RocketLaunchIcon />}
              label="Bonus Boosts"
              size="small"
              variant={isShrimp ? 'outlined' : undefined}
              sx={{
                bgcolor: isShrimp ? 'transparent' : withAlpha(theme.colors.secondary, 0.12),
                color: theme.colors.text,
                borderRadius: 'var(--radius-chip)',
                border: isShrimp ? `1px solid ${theme.colors.border}` : `1px solid ${withAlpha(theme.colors.secondary, 0.42)}`,
              }}
            />
          </Stack>
        </Box>



        {/* Cards grid */}
        <Grid container spacing={{ xs: 2.5, md: 3.5 }} justifyContent="center">
          {sortedLevels.map((lvl, index) => (
            <Grid item xs={12} sm={6} md={3} key={lvl.level}>
              <VipCard
                theme={theme}
                lvl={lvl}
                index={index}
                currentIndex={currentIndex}
                vipProgress={vipProgress}
                isMaxLevel={isMaxLevel}
                totalXP={totalXP}
                favoritesSet={favoritesSet}
                onToggleFavorite={onToggleFavoriteHaptic}
                onOpenDetails={openDetails}
              />
            </Grid>
          ))}
        </Grid>


      </Container>

      {/* Tier details dialog */}
      <Dialog
        open={!!selectedLevel}
        onClose={closeDetails}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        aria-labelledby="vip-details-title"
      >
        <DialogTitle
          id="vip-details-title"
          sx={{
            bgcolor: selectedLevel?.color,
            color: '#000',
            textAlign: 'center',
            fontWeight: 900,
            borderRadius: 'var(--radius-card)',
          }}
        >
          {selectedLevel?.level} Details
        </DialogTitle>
        <DialogContent sx={{ bgcolor: theme.colors.card, borderRadius: 'var(--radius-card)' }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Box sx={{ display: 'grid', placeItems: 'center' }}>
              <LevelIcon level={selectedLevel?.level} color={selectedLevel?.color} size={'3.2rem'} />
            </Box>
            <Typography variant="subtitle2" sx={{ color: theme.colors.text, mt: 1 }}>
              Required: {selectedLevel?.need.toLocaleString()} CPT
            </Typography>
            {selectedLevel?.rake > 0 && (
              <Chip
                icon={<CasinoIcon />}
                label={`${selectedLevel.rake}% Rakeback`}
                sx={{
                  mt: 1,
                  bgcolor: selectedLevel.color,
                  color: '#000',
                  borderRadius: 'var(--radius-chip)',
                }}
              />
            )}
          </Box>
          <List dense>
            {selectedLevel?.perks.map((perk, i) => (
              <ListItem key={i} sx={{ px: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Box sx={{ display: 'grid', placeItems: 'center', width: 24, height: 24 }}>
                    <PerkIcon text={perk.text} color={selectedLevel.color} />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={perk.text}
                  primaryTypographyProps={{ color: theme.colors.text }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            bgcolor: theme.colors.card,
            borderRadius: 'var(--radius-card)',
          }}
        >
          <Button
            onClick={closeDetails}
            variant="contained"
            size="small"
            sx={{
              bgcolor: selectedLevel?.color,
              color: '#000',
              fontWeight: 800,
              textTransform: 'none',
              borderRadius: 'var(--radius-button)',
              minHeight: 32,
              px: 1.25,
              letterSpacing: 0.1,
              boxShadow: 'none',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

VipClub.propTypes = {
  theme: PropTypes.object.isRequired,
  vipLevels: PropTypes.arrayOf(
    PropTypes.shape({
      level: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      need: PropTypes.number.isRequired,
      rake: PropTypes.number.isRequired,
      perks: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string.isRequired,
          icon: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ),
  userStats: PropTypes.shape({
    totalBets: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  isLoading: PropTypes.bool,
  onOpenTier: PropTypes.func,
};

VipClub.defaultProps = {
  vipLevels: [],
  userStats: undefined,
  isLoading: false,
  onOpenTier: undefined,
};

export default VipClub;
